<template>
  <span v-if="visible" class="sf-daterange-cont sf">
    <ElDatePicker
      v-model="oldInput"
      :type="`${time ? 'datetimerange' : 'daterange'}`"
      :start-placeholder="`${label}开始${unit}`"
      :end-placeholder="`结束${unit}`"
      :shortcuts="shortcuts"
      :format="`${time ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'}`"
      :value-format="`${time ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'}`"
      :default-time="defaultTime"
      unlink-panels
      class="sf-daterange"
    />
  </span>
</template>

<script lang="ts">
/**
 * ### 日期范围选择组件
 * 提交字符串格式为 /^YYYY-MM-DD(?: HH:mm:ss)?$/
 */
import { computed, Ref, defineComponent, PropType } from 'vue';
import { ElDatePicker } from 'element-plus';
import {
  buildArraySearchFieldProps,
  setupArraySearchField
} from '../SearchField';
import dayjs from 'dayjs';
import { isArray } from 'lodash-es';

const ValueCtor = String;

const props = buildArraySearchFieldProps(ValueCtor, {
  /**
   * ### 是否包含时间选择器
   */
  time: { type: Boolean, default: true },
  /**
   * ### 设置快捷选项
   */
  shortcuts: {
    type: Array as PropType<
      { text: string; days: number; toYesterday?: boolean }[]
    >
  }
} as const);

export default defineComponent({
  extends: ElDatePicker,
  props,
  setup(props, ctx) {
    return setupArraySearchField(
      ValueCtor,
      props,
      ctx,
      (params) => {
        const oneDayMill = 24 * 60 * 60 * 1000;

        const midNight = () => {
          const date = new Date();
          return new Date(date.getFullYear(), date.getMonth(), date.getDate());
        };

        const lastDays = (days: number) => {
          const md = +midNight();

          return [new Date(md - oneDayMill * days), new Date(md - 1000)];
        };

        const lastMoreDays = (days: number) => {
          const md = +midNight();
          if (days >= 0) {
            return [new Date(md - oneDayMill * (days - 1)), new Date()];
          } else {
            return [new Date(), new Date(md - oneDayMill * days)];
          }
        };

        let shortcuts = [
          {
            text: '今天',
            value: () => [midNight(), new Date()]
          },
          {
            text: '昨天',
            value: () => lastDays(1)
          },
          {
            text: '近三天',
            value: () => lastMoreDays(3)
          },
          {
            text: '近七天',
            value: () => lastMoreDays(7)
          }
        ];

        if (props.shortcuts && props.shortcuts.length) {
          shortcuts = props.shortcuts.map((item) => {
            const calfun = item.toYesterday ? lastDays : lastMoreDays;
            return {
              text: item.text,
              value: () => calfun(item.days)
            };
          });
        }

        // 根据label属性, 生成开始、结束的placeholder文字
        let label = props.label as string;
        let unit = '';
        if (label.endsWith('时间')) {
          label = label.substring(0, label.length - 2);
          unit = '时间';
        } else if (label.endsWith('日期')) {
          label = label.substring(0, label.length - 2);
          unit = '日期';
        }

        // 转成java接受的日期格式
        const { input } = params;
        const newInut = computed({
          get() {
            const value = input.value;
            if (!isArray(value) || value.length != 2) {
              return value;
            }

            // 转成java格式的日期
            return value;
          },
          set(dates: string[] | undefined) {
            input.value = dates;
          }
        });
        // 替换input
        params.input = newInut;

        const defaultTime: [Date, Date] = [
          new Date(2000, 1, 1, 0, 0, 0),
          new Date(2000, 2, 1, 23, 59, 59)
        ];

        return {
          shortcuts,
          defaultTime,
          label,
          unit,
          input: newInut,
          oldInput: input as Ref<any>
        };
      },
      {
        sendDefault: true,
        defaultTranslate: (dates: string[] | undefined) => {
          if (!dates || !dates.filter((item) => item != '').length) {
            return undefined;
          }

          let convertDates = [];
          const format = props.time ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
          for (let d of dates) {
            if (d === 'now') {
              convertDates.push(dayjs().format(format));
            } else {
              convertDates.push(dayjs(d).format(format));
            }
          }

          return convertDates;
        }
      }
    );
  }
});
</script>

<style lang="scss" scoped>
.sf-daterange-cont {
  grid-column-start: span 2;

  :deep(.sf-daterange) {
    box-sizing: border-box;
    width: 100% !important;
    height: var(--sf-line-height) !important;
    line-height: calc(var(--sf-line-height) - 2px);
    box-shadow: none !important;
    border-radius: var(--sf-border-radius);
    border: 1px solid var(--sf-border-color);
    justify-self: stretch;

    --el-font-size-base: var(--sf-font-size);

    &:hover,
    &.is-active {
      border-color: var(--sf-border-hover-color);
    }

    .el-range-input {
      height: var(--sf-line-height) !important;
      line-height: var(--sf-line-height) !important;
    }

    .el-input__prefix {
      --el-input-icon-color: #8f959e;
    }
  }
}
</style>
